import { inject, Injectable, signal } from '@angular/core';
import { NavController } from '@ionic/angular/common';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { OnboardingFlow, OnboardingStep } from '@core/models/onboarding';
import { ONBOARDING_STEPS } from '@core/onboarding/onboarding-machine';
import { environment } from '@environment/environment';

import { getMockStateForStep } from '../../components/admin-overlay/admin-mock-data';
import { AccountService } from '../account/account';
import { BiometricService } from '../biometric/biometric';
import { GroupService } from '../group/group';
import { HealthService } from '../health/health';
import { OnboardingStateService } from '../onboarding/onboarding-state';
import { OrderService } from '../order/order';
import { ToastService } from '../toast/toast';

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  private readonly accountService = inject(AccountService);
  private readonly groupService = inject(GroupService);
  private readonly healthService = inject(HealthService);
  private readonly biometricService = inject(BiometricService);
  private readonly onboardingService = inject(OnboardingStateService);
  private readonly orderService = inject(OrderService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly navController = inject(NavController);

  private readonly isUnlocked$ = signal<boolean>(false);
  readonly isUnlocked = this.isUnlocked$.asReadonly();

  toggleUnlock(unlocked: boolean) {
    if (!environment.production) {
      this.isUnlocked$.set(unlocked);
      if (unlocked) {
        this.toastService.info(
          this.translateService.instant('Admin.Admin'),
          this.translateService.instant('Admin.Admin_Mode_Enabled'),
          1000
        );
      }
    }
  }

  goTo(path: string[]) {
    this.navController.navigateRoot(path);
  }

  toggleBiometricsAvailable(available: boolean) {
    if (this.isUnlocked()) {
      this.biometricService.toggleBiometricsAvailable(available);
    }
  }

  toggleCompletedOnboarding(completedOnboarding: boolean) {
    if (this.isUnlocked()) {
      this.accountService.toggleCompletedOnboarding(completedOnboarding);
    }
  }

  toggleCompletedOnboardingLearning(completedOnboardingLearning: boolean) {
    if (this.isUnlocked()) {
      this.accountService.toggleCompletedOnboardingLearning(completedOnboardingLearning);
    }
  }

  toggleStartingLineComplete(complete: boolean) {
    if (this.isUnlocked()) {
      const userId = this.groupService.viewingUser()?.userId;
      if (userId) this.healthService.toggleStartingLineComplete(userId, complete);
    }
  }

  clearAvailabilityAddress() {
    if (this.isUnlocked()) {
      this.accountService.clearAvailabilityAddress();
    }
  }

  toggleAvailabilityResult(available: boolean) {
    if (this.isUnlocked()) {
      this.accountService.toggleAvailabilityResult(available);
    }
  }

  skipOnboarding() {
    if (this.isUnlocked()) {
      this.onboardingService.completeOnboarding();
      this.navController.navigateRoot(['home']);
    }
  }

  startOnboarding() {
    if (this.isUnlocked()) {
      // Dev tool: force a fresh flow. startOnboarding is a no-op while already onboarding, so reset first.
      this.onboardingService.completeOnboarding();
      this.onboardingService.startOnboarding(OnboardingFlow.Standard);
      this.onboardingService.updateState({ completedLearning: true });
      this.navController.navigateRoot(['onboarding', 'availability']);
    }
  }

  async clearCart() {
    if (!this.isUnlocked()) return;
    // One call now: start-over answers with the replacement cart and OrderService adopts it, so there is no
    // second read to race against the first.
    await firstValueFrom(this.orderService.startOverCart());
  }

  toggleNOf1Enabled(enabled: boolean) {
    if (this.isUnlocked()) {
      const userId = this.groupService.viewingUser()?.userId;
      if (userId) this.healthService.toggleNOf1Enabled(userId, enabled);
    }
  }

  clearOnboardingState() {
    if (this.isUnlocked()) {
      this.onboardingService.completeOnboarding();
      this.onboardingService.setCompletedLearning(false);
      this.navController.navigateRoot(['home']);
    }
  }

  jumpToOnboardingStep(step: OnboardingStep) {
    if (!this.isUnlocked()) return;

    const def = ONBOARDING_STEPS.find((d) => d.step === step);
    if (!def) return;

    // Dev tool: force a fresh flow before applying the mock. startOnboarding is a no-op while already
    // onboarding, so reset first.
    this.onboardingService.completeOnboarding();
    this.onboardingService.startOnboarding(OnboardingFlow.Standard);

    const mockState = getMockStateForStep(step);
    this.onboardingService.updateState({
      ...mockState,
      currentStep: step
    });

    this.navController.navigateRoot(def.route);
  }
}
